import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { pushEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Phone, Mail, MapPin, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error(t.contact.errRequired);
    if (!form.consent) return toast.error(t.contact.errConsent);
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success(t.contact.sent);
    } catch {
      toast.error(t.contact.failed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="relative py-24 bg-navy text-cream overflow-hidden">
      <div className="absolute inset-0 grain"></div>
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-gold/10 blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">{t.contact.tag}</p>
          <h2 className="font-display text-4xl sm:text-5xl leading-tight mb-5">
            {t.contact.title1}<br/>
            <span className="italic text-gold-soft">{t.contact.title2}</span>
          </h2>
          <p className="text-cream/75 text-base max-w-md mb-10">{t.contact.desc}</p>

          <div className="space-y-5">
            <a href={`tel:${t.phone.replace(/\s/g, '')}`} onClick={() => pushEvent('phone_click', { source: 'contact' })} className="flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-full bg-navy-mid border border-navy-line flex items-center justify-center group-hover:bg-gold group-hover:border-gold group-hover:text-navy-deep transition">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-cream/55">{t.contact.call}</div>
                <div className="font-display text-2xl">{t.phone}</div>
              </div>
            </a>
            <a href="mailto:mail@madeira.com.pl" onClick={() => pushEvent('email_click', { source: 'contact' })} className="flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-full bg-navy-mid border border-navy-line flex items-center justify-center group-hover:bg-gold group-hover:border-gold group-hover:text-navy-deep transition">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-cream/55">{t.contact.write}</div>
                <div className="font-display text-2xl">mail@madeira.com.pl</div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-navy-mid border border-navy-line flex items-center justify-center">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-cream/55">{t.contact.office}</div>
                <div className="text-base">{t.contact.address}</div>
              </div>
            </div>
          </div>

          <div className="mt-10 inline-flex items-center gap-3 bg-navy-mid/50 border border-navy-line rounded-full px-5 py-2.5">
            <span className="w-2 h-2 rounded-full bg-gold animate-pulse"></span>
            <span className="text-sm text-cream/85">{t.contact.license}</span>
          </div>
        </div>

        <div className="bg-navy-deep/80 backdrop-blur border border-navy-line rounded-2xl p-7 sm:p-9 text-cream shadow-2xl">
          {sent ? (
            <div data-testid="contact-success" className="text-center py-10">
              <h3 className="font-display text-3xl mb-3 text-gold">{t.contact.successTitle}</h3>
              <p className="text-cream/75">{t.contact.successDesc}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <h3 className="font-display text-2xl mb-3">{t.contact.formTitle}</h3>
              <div>
                <Label className="text-xs uppercase tracking-wider text-cream/75">{t.quiz.name} *</Label>
                <Input data-testid="contact-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="mt-1 bg-navy/60 border-navy-line text-cream" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-cream/75">{t.quiz.email} *</Label>
                  <Input data-testid="contact-email" type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="mt-1 bg-navy/60 border-navy-line text-cream" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-cream/75">{t.quiz.phone}</Label>
                  <Input data-testid="contact-phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} className="mt-1 bg-navy/60 border-navy-line text-cream" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-cream/75">{t.contact.message}</Label>
                <Textarea data-testid="contact-message" rows={4} value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} className="mt-1 bg-navy/60 border-navy-line text-cream placeholder:text-cream/40" placeholder={t.contact.messagePlaceholder} />
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox data-testid="contact-consent" checked={form.consent} onCheckedChange={(v)=>setForm({...form,consent:!!v})} className="mt-0.5 border-gold/50 data-[state=checked]:bg-gold data-[state=checked]:text-navy-deep" />
                <span className="text-xs text-cream/70">{t.contact.consent}</span>
              </label>
              <Button data-testid="contact-submit" type="submit" disabled={loading} className="btn-primary w-full py-6 text-base">
                {loading ? t.contact.sending : <>{t.contact.submit} <Send className="w-4 h-4 ml-2"/></>}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
